import {
  CheckCircle2,
  Award,
  Users,
  Sparkles,
  ArrowUpRight,
  Quote,
  Building2,
  Boxes,
  ShieldCheck,
  Activity,
  Layers,
} from "lucide-react";

const STATS = [
  { value: "10+", label: "Años en retail\nchileno" },
  { value: "100s", label: "Sucursales\nintervenidas" },
  { value: "24/7", label: "Compromiso\noperacional" },
  { value: "1", label: "Sola responsabilidad,\nla tuya" },
];

const VALORES = [
  {
    Icon: CheckCircle2,
    title: "Compromiso operacional",
    text:
      "Tu operación es nuestra prioridad. Respondemos como si fuera nuestra propia tienda.",
  },
  {
    Icon: Award,
    title: "Estándar alto",
    text:
      "El mismo nivel técnico que exigen los grandes retailers, aplicado en cada despliegue.",
  },
  {
    Icon: Users,
    title: "Cercanía real",
    text:
      "Estructura ágil, sin capas burocráticas. Hablas con quien resuelve.",
  },
  {
    Icon: Sparkles,
    title: "Sin atajos",
    text:
      "Equipos certificados, procesos documentados y reportes verificables.",
  },
];

const TERRENO = [
  {
    Icon: Building2,
    title: "Cadenas de supermercado",
    text: "Con cobertura y logística nacional.",
  },
  {
    Icon: Layers,
    title: "Retail multi-formato",
    text: "Grandes tiendas, especialidad y conveniencia.",
  },
  {
    Icon: Boxes,
    title: "Farmacias con red país",
    text: "Despliegues coordinados en cientos de locales.",
  },
  {
    Icon: Activity,
    title: "Operación crítica 24/7",
    text: "SLA estrictos, tiempos de respuesta acotados.",
  },
];

export const Nosotros = () => {
  return (
    <section
      id="nosotros"
      data-testid="nosotros-section"
      className="relative bg-white py-24 md:py-32 overflow-hidden"
    >
      {/* Subtle accents */}
      <div
        aria-hidden
        className="absolute top-20 -left-32 w-[420px] h-[420px] rounded-full bg-[#1FA08A]/[0.05] blur-[120px]"
      />
      <div
        aria-hidden
        className="absolute bottom-0 -right-32 w-[420px] h-[420px] rounded-full bg-[#0A2A4E]/[0.04] blur-[120px]"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="mb-12 md:mb-16">
          <div className="flex items-center gap-3 mb-6 text-[11px] uppercase tracking-[0.32em] font-mono">
            <span className="text-[#1FA08A] font-semibold">/ 05</span>
            <span className="h-px w-10 bg-slate-300" />
            <span className="text-slate-500 font-semibold">
              Nosotros · Manifiesto
            </span>
          </div>
          <h2
            data-testid="nosotros-heading"
            className="font-heading text-[#0A2A4E] text-4xl sm:text-5xl md:text-[56px] lg:text-[68px] leading-[1.02] tracking-tighter font-bold max-w-5xl"
          >
            Nuevos como marca.{" "}
            <span className="relative inline-block">
              <span className="text-[#1FA08A]">Veteranos</span>
              <svg
                aria-hidden
                viewBox="0 0 260 14"
                className="absolute -bottom-1 left-0 w-full h-3 text-[#1FA08A]/40"
                preserveAspectRatio="none"
              >
                <path
                  d="M2 10 C 60 2, 180 16, 258 6"
                  stroke="currentColor"
                  strokeWidth="3"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>{" "}
            en terreno.
          </h2>
        </div>

        {/* Story + Experience split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-20 md:mb-24">
          {/* Story column */}
          <div className="lg:col-span-7 space-y-6 lg:pr-8">
            <p className="text-lg md:text-xl text-slate-700 leading-relaxed font-light">
              <span className="text-[#0A2A4E] font-semibold">Betec</span> nace
              como una empresa joven, pero con más de{" "}
              <span className="text-[#0A2A4E] font-semibold">
                una década de oficio en el retail chileno
              </span>{" "}
              detrás de cada decisión.
            </p>
            <p className="text-base md:text-[17px] text-slate-600 leading-relaxed">
              Nuestro fundador acumuló esa experiencia en proyectos de
              infraestructura, POS, autoservicio y soporte para las cadenas
              más exigentes del país. Vimos cómo se opera a gran escala —
              cómo se evita una caída en hora peak, cómo se despliega en
              cientos de sucursales y cómo se certifica una red para que
              soporte la operación durante años.
            </p>
            <p className="text-base md:text-[17px] text-slate-600 leading-relaxed">
              Hoy ponemos ese estándar al servicio de empresas que necesitan{" "}
              <span className="text-[#0A2A4E] font-semibold">
                un proveedor cercano, técnico y comprometido
              </span>{" "}
              — sin la rigidez ni los costos de una multinacional, pero con
              la misma rigurosidad operacional.
            </p>

            {/* Stats grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-6 pt-8 border-t border-slate-200">
              {STATS.map((s, i) => (
                <div key={i} data-testid={`nosotros-stat-${i}`} className="flex flex-col">
                  <div className="font-heading text-[#0A2A4E] text-[42px] md:text-[52px] font-bold tracking-tighter leading-none">
                    {s.value}
                  </div>
                  <div className="mt-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 whitespace-pre-line leading-snug">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Experience card - navy */}
          <div className="lg:col-span-5">
            <div className="relative rounded-md bg-gradient-to-br from-[#0A2A4E] to-[#05162a] text-white p-7 md:p-9 overflow-hidden lg:sticky lg:top-32 border border-[#0A2A4E]">
              {/* Pixel accent */}
              <div
                aria-hidden
                className="absolute top-5 right-5 grid grid-cols-3 gap-1.5 opacity-55"
              >
                {[1, 1, 0, 1, 1, 1, 0, 1, 1].map((v, i) => (
                  <span
                    key={i}
                    className={`w-2 h-2 ${
                      v ? "bg-[#2bc9ae]" : "bg-transparent"
                    }`}
                  />
                ))}
              </div>

              {/* Teal glow */}
              <div
                aria-hidden
                className="absolute -bottom-20 -left-10 w-[260px] h-[260px] rounded-full bg-[#1FA08A]/30 blur-[100px]"
              />

              <div className="relative">
                <Quote className="w-7 h-7 text-[#2bc9ae] mb-5" strokeWidth={1.5} />

                <p className="text-[11px] uppercase tracking-[0.28em] font-mono font-semibold text-[#2bc9ae] mb-4">
                  Experiencia forjada en terreno
                </p>

                <p className="text-white text-[17px] md:text-lg leading-relaxed font-light mb-7">
                  Una década operando la infraestructura que sostiene al
                  retail chileno. El estándar no lo aprendimos en aula —{" "}
                  <span className="text-[#2bc9ae] font-semibold">
                    lo forjamos en piso de venta.
                  </span>
                </p>

                {/* Terreno list */}
                <ul
                  data-testid="nosotros-terreno"
                  className="space-y-3 mb-2"
                >
                  {TERRENO.map((t) => (
                    <li
                      key={t.title}
                      className="flex items-start gap-3 group/item"
                    >
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-white/[0.06] border border-white/10 text-[#2bc9ae] shrink-0 mt-0.5 group-hover/item:bg-[#1FA08A] group-hover/item:text-white group-hover/item:border-[#1FA08A] transition-colors">
                        <t.Icon className="w-4 h-4" strokeWidth={1.6} />
                      </span>
                      <div>
                        <div className="font-heading text-[15.5px] md:text-base font-semibold text-white leading-tight">
                          {t.title}
                        </div>
                        <div className="text-[12.5px] text-slate-300/75 leading-snug mt-0.5">
                          {t.text}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Valores grid */}
        <div>
          <div className="flex items-center gap-3 mb-8 text-[11px] uppercase tracking-[0.32em] font-mono">
            <span className="text-[#1FA08A] font-semibold">— Valores</span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {VALORES.map(({ Icon, title, text }, i) => (
              <article
                key={i}
                data-testid={`nosotros-valor-${i}`}
                className="group relative bg-white border border-slate-200 rounded-md p-7 transition-all duration-500 hover:border-[#1FA08A]/60 hover:shadow-[0_24px_60px_-30px_rgba(10,42,78,0.35)] hover:-translate-y-1 overflow-hidden"
              >
                <span
                  aria-hidden
                  className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#1FA08A] scale-y-0 origin-top group-hover:scale-y-100 transition-transform duration-500"
                />
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-md bg-[#0A2A4E]/[0.04] text-[#0A2A4E] mb-5 transition-all duration-500 group-hover:bg-[#1FA08A] group-hover:text-white">
                  <Icon className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-[#0A2A4E] text-[19px] md:text-[20px] font-semibold tracking-tight leading-tight mb-2.5">
                  {title}
                </h3>
                <p className="text-[14px] text-slate-600 leading-relaxed">
                  {text}
                </p>
              </article>
            ))}
          </div>
        </div>

        {/* Closing line + CTA */}
        <div className="mt-16 md:mt-20 flex flex-col md:flex-row items-start md:items-end justify-between gap-6 border-t border-slate-200 pt-10">
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#1FA08A] font-mono font-semibold mb-3">
              Construyamos juntos
            </p>
            <p className="font-heading text-[#0A2A4E] text-2xl md:text-3xl font-semibold tracking-tight leading-snug">
              ¿Necesitas un partner técnico que entienda la operación tanto
              como tú?
            </p>
          </div>
          <a
            href="#contacto"
            data-testid="nosotros-cta"
            className="cta-cotizar group inline-flex items-center gap-2.5 bg-[#0A2A4E] hover:bg-[#153d6e] text-white px-7 py-3.5 rounded-sm font-semibold text-[14px] tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-md hover:shadow-xl whitespace-nowrap"
          >
            Conversemos
            <span aria-hidden className="cta-dot" />
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Nosotros;
